/**
 * The search area of the search page (02_Search_Mobile/SPEC.md "Search radius"): a point and a
 * radius in miles. The listing query uses the circle's bounding box, and the listings in the
 * box's corners (outside the circle) are left out on the client.
 */
import { types as sdkTypes } from './sdkLoader';

const { LatLng, LatLngBounds } = sdkTypes;

export const RADIUS_PRESETS_MI = [5, 10, 25];
export const DEFAULT_RADIUS_MI = 10;
export const MIN_RADIUS_MI = 1;
export const MAX_RADIUS_MI = 100;

const EARTH_RADIUS_MI = 3958.8;
const MILES_PER_DEGREE_LAT = 69.0;
const toRadians = deg => (deg * Math.PI) / 180;

/**
 * Keep a radius within 1–100 miles, as a whole number. Anything that isn't a number gives the
 * default radius.
 *
 * @param {number|string} radius
 * @returns {number} miles
 */
export const validRadius = radius => {
  const value = Math.round(Number(radius));
  if (!Number.isFinite(value) || value <= 0) {
    return DEFAULT_RADIUS_MI;
  }
  return Math.min(Math.max(value, MIN_RADIUS_MI), MAX_RADIUS_MI);
};

/**
 * Distance between two points along the earth's surface (haversine), in miles.
 *
 * @param {{lat: number, lng: number}} a
 * @param {{lat: number, lng: number}} b
 * @returns {number} miles
 */
export const distanceInMiles = (a, b) => {
  const dLat = toRadians(b.lat - a.lat);
  const dLng = toRadians(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(a.lat)) * Math.cos(toRadians(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_MI * Math.asin(Math.min(1, Math.sqrt(h)));
};

/**
 * Is the point within the circle?
 *
 * @param {{lat: number, lng: number}} point
 * @param {{lat: number, lng: number}} center
 * @param {number} radiusMi
 * @returns {boolean}
 */
export const isWithinRadius = (point, center, radiusMi) =>
  !!point && !!center && distanceInMiles(center, point) <= radiusMi;

// How many degrees of latitude and longitude the radius spans at the center
const degreeSpans = (center, radiusMi) => {
  const latSpan = radiusMi / MILES_PER_DEGREE_LAT;
  const cosLat = Math.max(Math.cos(toRadians(center.lat)), 0.01);
  const lngSpan = radiusMi / (MILES_PER_DEGREE_LAT * cosLat);
  return { latSpan, lngSpan };
};

const round = (value, decimals = 6) => Number(value.toFixed(decimals));

/**
 * The bounding box of the circle, as SDK bounds for the listing query.
 *
 * @param {{lat: number, lng: number}} center
 * @param {number} radiusMi
 * @returns {LatLngBounds}
 */
export const circleBounds = (center, radiusMi) => {
  const { latSpan, lngSpan } = degreeSpans(center, radiusMi);
  return new LatLngBounds(
    new LatLng(round(Math.min(center.lat + latSpan, 89.9)), round(center.lng + lngSpan)),
    new LatLng(round(Math.max(center.lat - latSpan, -89.9)), round(center.lng - lngSpan))
  );
};

/**
 * The middle of a bounding box.
 *
 * @param {{ne: {lat, lng}, sw: {lat, lng}}} bounds
 * @returns {LatLng|null}
 */
export const boundsCenter = bounds => {
  if (!bounds?.ne || !bounds?.sw) {
    return null;
  }
  const { ne, sw } = bounds;
  return new LatLng(round((ne.lat + sw.lat) / 2), round((ne.lng + sw.lng) / 2));
};

/**
 * The circle as a GeoJSON polygon, for drawing it on the map.
 *
 * @param {{lat: number, lng: number}} center
 * @param {number} radiusMi
 * @param {number} steps how many points make the circle
 * @returns {Object} GeoJSON Feature
 */
export const circlePolygon = (center, radiusMi, steps = 64) => {
  const { latSpan, lngSpan } = degreeSpans(center, radiusMi);
  const coordinates = [];
  for (let i = 0; i <= steps; i++) {
    const angle = (i / steps) * 2 * Math.PI;
    coordinates.push([
      center.lng + lngSpan * Math.cos(angle),
      center.lat + latSpan * Math.sin(angle),
    ]);
  }
  return {
    type: 'Feature',
    properties: {},
    geometry: { type: 'Polygon', coordinates: [coordinates] },
  };
};
